import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ShieldCheck, AlertCircle, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerificaEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) navigate('/registrati');
    inputRefs.current[0]?.focus();
  }, [email, navigate]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) { setError('Inserisci il codice completo a 6 cifre'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify?email=${encodeURIComponent(email)}&code=${fullCode}`);
      toast.success('Email verificata con successo!');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codice non valido');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-code?email=${encodeURIComponent(email)}`);
      toast.success('Nuovo codice inviato alla tua email');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'invio');
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md shadow-[var(--shadow-md)]">
        <CardHeader className="text-center">
          <div className="w-14 h-14 bg-[#238A92]/10 rounded-2xl flex items-center justify-center text-[#238A92] mx-auto mb-2">
            <ShieldCheck size={28} />
          </div>
          <CardTitle className="text-2xl" style={{fontFamily:'Space Grotesk'}}>Verifica la tua Email</CardTitle>
          <CardDescription>
            Abbiamo inviato un codice a 6 cifre a<br />
            <strong className="text-[#1F2A37]">{email}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle size={16} />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Code input */}
          <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
            {code.map((digit, i) => (
              <Input
                key={`digit-${i}`}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-[#238A92]"
              />
            ))}
          </div>

          <Button
            onClick={handleVerify}
            disabled={loading || code.join('').length !== 6}
            className="w-full bg-[#238A92] hover:bg-[#0F5E66] text-white mb-4"
          >
            {loading ? 'Verifica in corso...' : 'Verifica'}
          </Button>

          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">Non hai ricevuto il codice?</p>
            <Button variant="ghost" size="sm" onClick={handleResend} disabled={resending}>
              <RotateCcw size={14} className="mr-1" /> {resending ? 'Invio in corso...' : 'Invia nuovo codice'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
